import type { ReactNode } from 'react'
import { Suspense, useMemo } from 'react'
import { useNavigate, useParams, Navigate } from 'react-router'
import { FormattedMessage, useIntl } from 'react-intl'
import { usePageConfig } from '@hooks/usePageConfig'
import ErrorBoundary from '@components/ErrorBoundary/ErrorBoundary'
import ErrorMessage from '@components/ErrorBoundary/ErrorMessage'
import Loader from '@components/Loader/Loader'
import ExternalHeaderContent from '@components/ExternalHeaderContent/ExternalHeaderContent'
import { useDashboard } from '@hooks/useDashboard'
import DashboardGridSkeleton from '@components/skeletons/DashboardGridSkeleton/DashboardGridSkeleton'
import { useResourcePermissions } from '@hooks/useResourcePermissions'
import DashboardContent from '@components/DashboardContent/DashboardContent'
import DashboardUnavailable from '@components/DashboardContent/DashboardUnavailable/DashboardUnavailable'

import './Dashboard.css'

interface DashboardBreadcrumbProps {
  id: string
  headerActions: ReactNode
}

const DashboardBreadcrumb = ({
  id,
  headerActions
}: DashboardBreadcrumbProps) => {
  const intl = useIntl()
  const item = useDashboard(id)
  const { canEdit } = useResourcePermissions(item?.owner.id)

  const crumbs = [
    {
      label: intl.formatMessage({ id: 'nav.dashboards' }),
      path: '/dashboards'
    },
    { label: item?.title ?? id, path: `/dashboards/${id}` }
  ]
  return (
    <ExternalHeaderContent
      hasBack
      crumbs={crumbs}
      headerActions={canEdit ? headerActions : undefined}
    />
  )
}

export default function Dashboard() {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()

  const headerActions = useMemo(
    () =>
      id ? (
        <button
          className="dashboard-button highlight-button"
          onClick={() => navigate(`/dashboard-builder/${id}`)}
        >
          <span className="icon icon-pen" />
          <span className="text only-desktop">
            <FormattedMessage id="dashboard-button.edit" />
          </span>
        </button>
      ) : null,
    [id, navigate]
  )

  const headerContent = useMemo(
    () =>
      id ? (
        <ErrorBoundary errorComponent={ErrorMessage}>
          <Suspense fallback={<Loader mini />}>
            <DashboardBreadcrumb id={id} headerActions={headerActions} />
          </Suspense>
        </ErrorBoundary>
      ) : null,
    [id, headerActions]
  )

  usePageConfig('dashboard', headerContent)

  if (!id || id === 'new') return <Navigate to="/dashboards" replace />

  return (
    <ErrorBoundary errorComponent={DashboardUnavailable}>
      <Suspense fallback={<DashboardGridSkeleton />}>
        <DashboardContent id={id} />
      </Suspense>
    </ErrorBoundary>
  )
}
